/**
 * Logger 日志打印函数
 *
 * @authors Luo-jinghui (luojinghui424@gmail.com)
 * @date  2019-10-22 20:08:33
 */
import { getMillTime, getTime } from "./utils";
import Queue from "./queue";
import {
  appendContent,
  createDir,
  watchFile,
  unWatchFile,
  readDir,
  copyFile,
  clearFile,
} from "./file";

const MAX_COUNT = 10; // 最多生成10个日志文件
const MEMORY_SIZE = 10 * 1024 * 1024; // 每个文件最大10M(单位 字节)

interface ILogLevel {
  INFO: "INFO";
  DEBUG: "DEBUG";
  WARNING: "WARNING";
  ERROR: "ERROR";
  NONE: "NONE";
}

class Logger {
  public LogLevel: ILogLevel;
  public currentLevel: string;
  // 文件名
  public fileName: string;
  // 日志目录
  public fileDirectory: string;
  // 日志文件所在路径
  public filePath: string;
  // 任务队列
  private taskQueue: Queue<any> | null;
  // 日志文件数量
  private currentFileLength: number;
  // 监听变化interval
  private watchFileInterval: number;

  private initCallback?: ()=>void;

  constructor(sdkLogDir, sdkLogPath, logName, initCallback) {
    this.LogLevel = {
      INFO: "INFO",
      DEBUG: "DEBUG",
      WARNING: "WARNING",
      ERROR: "ERROR",
      NONE: "NONE",
    };
    this.currentLevel = this.LogLevel.NONE;

    this.fileName = logName;
    this.fileDirectory = sdkLogDir;
    this.filePath = sdkLogPath;
    this.taskQueue = new Queue();
    this.currentFileLength = 0;
    this.watchFileInterval = 5000;
    this.initCallback = initCallback;

    this.taskQueue.addTask(() => this.init());
  }

  /**
   * 初始化SDK日志文件和目录
   */
  async init() {
    await createDir(this.fileDirectory);
    this.watchFile();

    const list: any = await readDir(this.fileDirectory, this.fileName);
    const fileLength = list.length - 1;

    this.currentFileLength = fileLength < 0 ? 0 : fileLength;

    this.initCallback?.();
  }

  /**
   * 监听文件变化，并缓存文件
   */
  watchFile() {
    watchFile(this.filePath, this.watchFileInterval, (size) => {
      if (size > MEMORY_SIZE) {
        this.taskQueue.addTask(() => this.cacheFile());
      }
    });
  }

  /**
   * 缓存文件：先copy文件，再清空原log文件
   */
  async cacheFile() {
    const len = this.getCacheFileCount();
    const cacheFilePath = `${this.filePath}.${len}`;

    await copyFile(this.filePath, cacheFilePath);
    await clearFile(this.filePath);
  }

  /**
   * 获取当前需要循环写入的文件Length
   *
   * @returns { number } - 缓存日志文件Length,从1-MAX_COUNT的值
   */
  getCacheFileCount() {
    const fileLength = this.currentFileLength;

    if (fileLength >= MAX_COUNT) {
      this.currentFileLength = 1;
    } else {
      this.currentFileLength += 1;
    }

    return this.currentFileLength;
  }

  /**
   * 取消监听文件变化
   */
  unWatch() {
    unWatchFile(this.filePath);
  }

  /**
   * 追加日志内容
   *
   * @param { string } data - 日志内容
   */
  async appendContent(data) {
    this.taskQueue.addTask(() => appendContent(this.filePath, data));
  }

  /**
   * 设置日志等级
   *
   * @param { string } level - 日志等级
   */
  public setLogLevel = (level: string) => {
    this.currentLevel = level;
  };

  /**
   * 向日志文件中追加内容
   *
   * @param { string } title - 日志标题
   * @param { any } content - 日志内容
   * @param { any } rest - 剩余内容
   */
  public putLog = (title, content, rest) => {
    const data = { t: title, c: content, r: rest };
    const localTime = getTime();

    try {
      const jsonData = JSON.stringify(data);
      const wrapTimerData = `[${localTime}][js]${jsonData}`;

      this.appendContent(wrapTimerData);
    } catch (error) {
      console.log("putLog error: ", error);
    }
  };

  // 输出所有 API 日志信息
  public debug(title: any = "", content: any = "", ...rest) {
    const { DEBUG } = this.LogLevel;

    // 单独过滤下meeting stats数据
    if (this.currentLevel === DEBUG && title !== "meeting-stats") {
      console.log(`[${getMillTime()}][DEBUG]`, title, content, ...rest);
    }

    this.putLog(title, content, rest);
  }

  // 输出 INFO、WARNING、ERROR 级别的日志信息
  public log(title: any, content: any = "", ...rest) {
    const { INFO, DEBUG } = this.LogLevel;

    if (this.currentLevel === DEBUG || this.currentLevel === INFO) {
      console.log(`[${getMillTime()}][INFO]`, title, content, ...rest);
    }

    this.putLog(title, content, rest);
  }

  // 输出 WARNING 和 ERROR 级别的日志信息
  public warn(title: any, content: any = "", ...rest) {
    const { INFO, DEBUG, WARNING } = this.LogLevel;

    if (
      this.currentLevel === DEBUG ||
      this.currentLevel === INFO ||
      this.currentLevel === WARNING
    ) {
      console.log(`[${getMillTime()}][WARN]`, title, content, ...rest);
    }

    this.putLog(title, content, rest);
  }

  // 输出 ERROR 级别的日志信息
  public error(title: any, content: any = "", ...rest) {
    const { INFO, DEBUG, ERROR, WARNING } = this.LogLevel;

    if (
      this.currentLevel === DEBUG ||
      this.currentLevel === ERROR ||
      this.currentLevel === INFO ||
      this.currentLevel === WARNING
    ) {
      console.log(`[${getMillTime()}][ERROR]`, title, content, ...rest);
    }

    this.putLog(title, content, rest);
  }

  // public addTask(){
  //   this.taskQueue.addTask() 
  // }

  /**
   * Upload sdk log file to redmine platform
   * redmine url: http://172.20.33.81:8080/redmine/projects/crashandupload/issues
   *
   * @param name file's name
   */
  public async uploadLog(name: string = "") {
    // if (this.isArrowUpload) {
    //   await this.db.tempStart();
    //   const blob = await this.db.upload(name);
    //   const formData = new FormData();
    //   const random = Math.ceil(Math.random() * 10000) + "";
    //   const dataNow = Date.now() + "";
    //   formData.append("sn", random);
    //   formData.append("userId", random);
    //   formData.append("deviceId", random);
    //   formData.append("deviceType", "WebrtcSDK");
    //   formData.append("userDisplayName", name);
    //   formData.append("deviceDisplayName", navigator.userAgent);
    //   formData.append("dataType", "userSubmit");
    //   formData.append("account", "");
    //   formData.append("number", "");
    //   formData.append("softwareVersion", "");
    //   formData.append("osVersion", "");
    //   formData.append("deviceModel", "");
    //   formData.append("crashType", "UI");
    //   formData.append("crashTime", dataNow);
    //   formData.append("suffixName", ".zip");
    //   formData.append("file", blob, "webrt_log.zip");
    //   try {
    //     // const result = await action.upload(formData);
    //     // return result && (result.status >= 200 || result.status < 300);
    //   } catch (err) {
    //     return false;
    //   }
    // }
  }
}

export default Logger;
